import type { PropertyIDB } from '@utils/indexeddb.utils';
import localforage from 'localforage';

const generations = new Map<string, number>();
const mutations = new Map<string, Promise<void>>();

function withCacheLock<T>(propertyId: string, operation: () => Promise<T>): Promise<T> {
  const result = (mutations.get(propertyId) ?? Promise.resolve()).then(operation);
  // A failed operation must not block later reads, writes or invalidations for this property.
  mutations.set(
    propertyId,
    result.then(
      () => undefined,
      () => undefined
    )
  );
  return result;
}

export const getRegionTreeCacheGeneration = (propertyId: string): number =>
  generations.get(propertyId) ?? 0;

export function invalidateRegionTreeCache(propertyId: string): Promise<void> {
  // Invalidate synchronously, including requests still waiting for a cache read or HTTP response.
  generations.set(propertyId, getRegionTreeCacheGeneration(propertyId) + 1);
  return withCacheLock(propertyId, async () => {
    const prefix = `region-tree:${propertyId}:`;
    const keys = await localforage.keys();
    const entries = new Set([propertyId, ...keys.filter((key) => key.startsWith(prefix))]);
    await Promise.all([...entries].map((key) => localforage.removeItem(key)));
  });
}

export function readRegionTreeCache(propertyId: string, key: string): Promise<PropertyIDB | null> {
  return withCacheLock(propertyId, () => localforage.getItem<PropertyIDB>(key));
}

export function writeRegionTreeCache(
  propertyId: string,
  key: string,
  generation: number,
  value: PropertyIDB
): Promise<void> {
  return withCacheLock(propertyId, async () => {
    if (generation !== getRegionTreeCacheGeneration(propertyId)) return;
    await localforage.setItem(key, value);
  });
}
